 
import React, { useContext, useMemo, useState } from 'react';
import { useRef, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import Text from 'v3/components/Text';
import Card, { DefaultLoadingBodyComponent } from 'v3/components/Card';
import { useTravel } from 'v3/pages/Travel';
import { formatDateTime } from 'v3/utils/formatter';
import Button from 'v3/components/Button';
import { usePermission } from 'hooks';
import { BlockedTravelContext } from 'contexts/BlockedTravelProvider';
import * as S from './styles';
import IntegrationSmModal from './IntegrationSM/IntegrationSmModal';

function PreSMContent({ preSM, data }) {
  const currentCode = useMemo(() => {
    return preSM?.presm?.codSM || preSM?.presm?.codPreSM || '0';
  }, [preSM]);

  const logsDaSmAtual = useMemo(() => {
    return (
      preSM?.logs?.filter(
        (log) => String(log.cod_sm || log.cod_pre_sm || '0') === String(currentCode),
      ) || []
    );
  }, [preSM, currentCode]);

  const logMaisRecente = useMemo(() => {
    if (!logsDaSmAtual.length) return null;
    return [...logsDaSmAtual].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))[0];
  }, [logsDaSmAtual]);

  const trackStatus = useMemo(() => {
    if (data?.lastLog?.logStatus) return data.lastLog.logStatus;
    if (logMaisRecente?.log_status) return logMaisRecente.log_status;
    return 'Viagem não possui SM ativa';
  }, [data, logMaisRecente]);

  const lastUpdatedAt = data?.lastLog?.updatedAt || logMaisRecente?.updatedAt || null;
  const codSM = data?.presm?.codSM || logMaisRecente?.cod_sm || null;

  if (!data) {
    return (
      <Col xs={12}>
        <Text type="regular" color="gray" as="p" className="mt-2">
          Falha ao consultar informações de rastreio da viagem
        </Text>
      </Col>
    );
  }
  return (
    <>
      <Col md={6} xs={12}>
        <Text type="label" color="dark">
          Status do rastreamento
        </Text>
        <Text type="regular" color="gray" as="p" className="mt-2">
          {trackStatus}
        </Text>
      </Col>
      <Col md={6} xs={12}>
        <Text type="label" color="dark">
          Data da última alteração
        </Text>
        <Text type="regular" color="gray" as="p" className="mt-2">
          {lastUpdatedAt ? formatDateTime(lastUpdatedAt) : 'Não informado'}
        </Text>
      </Col>
      <Col md={6} xs={12}>
        <Text type="label" color="dark">
          Data da última localização
        </Text>

        <Text type="regular" color="gray" as="p" className="mt-2">
          {data?.lastLocation?.capturedAt
            ? formatDateTime(data?.lastLocation?.capturedAt)
            : 'Veículo sem posição'}
        </Text>
      </Col>
      <Col md={6} xs={12}>
        <Text type="label" color="dark">
          Código Solicitação
        </Text>
        <Text type="regular" color="gray" as="p" className="mt-2">
          {codSM || 'Não informado'}
        </Text>
      </Col>
      {data?.lastLog?.message && data?.lastLog?.message?.length > 0 && (
        <Col xs={12}>
          <Text type="label" color="dark">
            Problemas encontrados
          </Text>
          <S.List className="pl-3">
            {data?.lastLog?.message?.map((error) => (
              <li>
                <Text type="regular" color="gray" as="p" className="d-block mb-2">
                  {error}
                </Text>
              </li>
            ))}
          </S.List>
        </Col>
      )}
      {data?.inconsistencies && data?.inconsistencies.length > 0 && (
        <Col xs={12}>
          <Text type="label" color="dark">
            Inconsistências raster
          </Text>
          <S.List className="pl-3">
            {data?.inconsistencies.map((inconsistencie) => (
              <li>
                <Text type="regular" color="gray" as="p" className="d-block mb-2">
                  {inconsistencie.message}
                </Text>
              </li>
            ))}
          </S.List>
        </Col>
      )}
    </>
  );
}

function PreSMStatus({ getLoad }) {
  const { isTravelBlocked } = useContext(BlockedTravelContext);
  const { data, preSM, fetchPreSM, retrieveOccurrences, isLoading, fetchFeed } = useTravel();
  const [loading, setLoading] = useState(false);
  const [showSmModal, setShowSmModal] = useState(false);
  const showPreSMPermission = usePermission('VISUALIZAR_PRESM');
  const createPreSMPermission = usePermission('SOLICITAR_PRESM');
  const prevStatusIdRef = useRef();

  useEffect(() => {
    const cancelledStatusIds = [9, 11];

    const wasNotCancelledBefore = !cancelledStatusIds.includes(prevStatusIdRef.current);
    const isNowCancelled = cancelledStatusIds.includes(data.status_id);

    if (wasNotCancelledBefore && isNowCancelled) {
      const fetchOnCancel = async () => {
        setLoading(true);
        await fetchPreSM();
        if (getLoad) await getLoad();
        setLoading(false);
      };
      fetchOnCancel();
    }

    prevStatusIdRef.current = data.status_id;
  }, [data.status_id]);

  const showBtn = useMemo(() => {
    const finished = [5, 6, 7, 10];
    if (finished.includes(data.status_id)) return false;
    if (!createPreSMPermission) return false;
    return true;
  }, [data, createPreSMPermission]);

  if (!showPreSMPermission) return <></>;

  return (
    <Card
      loading={isLoading}
      LoadingBodyComponent={() => <DefaultLoadingBodyComponent linesCount={4} perLine={2} />}
      header={
        <Text color="dark" type="header">
          Rastreamento viagem
        </Text>
      }
      HeaderRightComponent={
        <>
          {showBtn && (
            <div>
              <Button
                variant="primary"
                className="mr-2"
                onClick={() => setShowSmModal(true)}
                onRefresh={fetchPreSM}
                loading={loading}
                disabled={isTravelBlocked}
              >
                <Text color="white" type="regular" weight="500">
                  Integração SM
                </Text>
              </Button>

              <IntegrationSmModal
                open={showSmModal}
                onClose={async () => {
                  setShowSmModal(false);
                  setLoading(true);
                  await fetchPreSM();
                  if (getLoad) await getLoad();
                  setLoading(false);
                }}
                travelData={data}
                smData={preSM?.presm}
                preSMData={preSM}
                onRefresh={async () => {
                  setLoading(true);
                  await fetchPreSM();
                  if (getLoad) await getLoad();
                  setLoading(false);
                }}
                getLoad={getLoad}
              />
            </div>
          )}
        </>
      }
    >
      <Row>
        <Col xs={12}>
          <div className="container-fluid">
            <Row className="form">
              <PreSMContent preSM={preSM} data={data} />
            </Row>
          </div>
        </Col>
      </Row>
    </Card>
  );
}
export default PreSMStatus;
